import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { environment } from '@env/environment.local';
import { ImagesState } from './store/images.state';
import {
    ClearDeletedImages,
    DeleteImage,
    GetFilesDocs,
    GetFilesDocsSize,
    GetImagesList,
    GetOptimizationConfig,
    ResetUploadedFiles,
    SetFilters,
    SetOptimizationConfig,
    UploadPhoto,
} from './store/images.actions';
import { Select, Store } from '@ngxs/store';
import { UploadImagesComponent } from './components/upload-images/upload-images.component';
import { Observable, debounceTime } from 'rxjs';
import {
    DeleteImages,
    FileItem,
    ImageItem,
    ImageManagerData,
    ImagesFilter,
    MODULES_OPTIONS,
    PRIORITY,
    SizeStatus,
    UploadedItem,
} from '@app/_models/images-manager/images-management.model';
import { PageEvent } from '@angular/material/paginator';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { McCropperComponent } from '../image-cropper/cropper.component';
import { UsedImagesModalComponent } from './components/used-images-modal/used-images-modal.component';
import { SsConfirmComponent } from '@app/components/ss-confirm/ss-confirm.component';
import { CarouselModalComponent } from './components/carousel-modal/carousel-modal.component';
import { ImagePreviewModalComponent } from './components/image-preview-modal/image-preview-modal.component';
import { McToastService } from '@app/_services';
import { ThemePalette } from '@angular/material/core';
import { ClipboardService } from 'ngx-clipboard';

export enum FILTER_TYPE {
    MODULE = 'MODULE',
    PRIORITY = 'PRIORITY',
}

export enum FILE_TYPE_FILTER {
    COMPRIMIDOS = 'compressed',
    DOCUMENTOS = 'document',
    IMAGENES = 'image',
    MULTIMEDIA = 'multimedia',
    ARCHIVO_DE_DATOS = 'datafile',
}

export interface IMAGE_MANAGER_DIALOG_DATA {
    module: MODULES_OPTIONS;
    relation_id: number;
    singleSelection?: boolean;
    isFileUpload?: boolean
}

@Component({
    selector: 'app-img-modal',
    templateUrl: './img-modal.component.html',
    styleUrls: ['./img-modal.component.scss'],
})
export class ImgModalComponent {
    assetsUrl: string = environment.assets_url;
    mfsPath: string = environment.mfs_path;
    loading: boolean = false;
    viewMode: 'grid' | 'list' = 'grid';
    // Diccionario para manejar el estado de fallback de cada imagen
    fallbackStatus: { [key: number]: boolean } = {};

    @Select(ImagesState.getImageList)
    imageList$: Observable<ImageItem[]>;

    imageList: ImageItem[];

    @Select(ImagesState.getImageListSize)
    imageListSize$: Observable<number>;

    imageListSize: number;

    @Select(ImagesState.getImagesFilter)
    imagesFilter$: Observable<ImagesFilter>;

    imagesFilter: ImagesFilter;

    @Select(ImagesState.getShowEmptyState)
    showEmptyState$: Observable<boolean>;

    pageSizeOptions = [5, 10, 25];
    imagesPaginator = {
        length: 0,
        pageSize: this.pageSizeOptions[1],
        pageIndex: 0,
    };

    searchForm: UntypedFormGroup;

    optimizationForm: UntypedFormGroup;

    @Select(ImagesState.getOptimizationConfig)
    optimizationConfig$: Observable<boolean>;

    filtering: boolean = false;

    modulesOptions: { id: MODULES_OPTIONS; name: string; disabled: boolean }[] = [];

    priorityOptions: { id: PRIORITY; name: string; disabled: boolean }[] = [];

    filtertType = FILTER_TYPE;

    selectedFiles: Set<ImageItem> = new Set();

    addedFiles: UploadedItem[] = [];

    singleSelection: boolean = false;

    firstLoad: boolean = true;

    currentModule: MODULES_OPTIONS;

    emptyStateImg: string = '/img/front/images/emptyImageManager.svg';
    emptyStateFile: string = '/img/front/files/empty_file_upload.svg';

    /**
     * Para archivos
     */
    isFileUpload: boolean;

    acceptedFileTypes = [
    '.pdf', '.txt', '.docx', '.doc', '.xlsx', '.xls', '.ppt', '.pptx', '.odt', '.ods', '.rtf',
    '.zip', '.rar', '.gz', '.7z',
    '.jpeg', '.jpg', '.png', '.gif', '.webp',
    '.mp3', '.mp4', '.wav', '.avi', '.mov',
    '.csv', '.json', '.xml', '.html', '.css'
    ];

    files: { file: File; url: string }[] = [];
    selectedFilesItems: Set<{ file: File; url: string }> = new Set(); 

    isFileUploadWindowOpen: boolean = false;

    @Select(ImagesState.getSizeData)
    sizeData$: Observable<SizeStatus>;

    sizeStatus: SizeStatus = {
        used: 0,
        free: 0,
        max: 0,
        usedPercentage: 0,
        usedMB: 0,
        maxGB: 0
    }

    progressColor: ThemePalette = 'primary'; // por defecto
    progressBarClass: string = ''; // clase CSS personalizada para naranja
    filesPaginator = {
        length: 0,
        pageSize: this.pageSizeOptions[1],
        pageIndex: 0,
    };
    @Select(ImagesState.getFilesFilter)
    filesFilter$: Observable<ImagesFilter>;

    filesFilter: ImagesFilter;
    @Select(ImagesState.getFileListSize)
    fileListSize$: Observable<number>;

    fileListSize: number;
    @Select(ImagesState.getFileList)
    fileList$: Observable<FileItem[]>;

    fileList: FileItem[];

    timedOutCloser;

    selectedChipId: string | null = null;

    fileTypeChips = [
        { id: FILE_TYPE_FILTER.COMPRIMIDOS, label: this.translate.instant('app.global.modals.image_manager.filters.chips_options.comprimidos') },
        { id: FILE_TYPE_FILTER.DOCUMENTOS, label: this.translate.instant('app.global.modals.image_manager.filters.chips_options.documentos') },
        { id: FILE_TYPE_FILTER.IMAGENES, label: this.translate.instant('app.global.modals.image_manager.filters.chips_options.imagenes') },
        { id: FILE_TYPE_FILTER.MULTIMEDIA, label: this.translate.instant('app.global.modals.image_manager.filters.chips_options.multimedia') },
        { id: FILE_TYPE_FILTER.ARCHIVO_DE_DATOS, label: this.translate.instant('app.global.modals.image_manager.filters.chips_options.archivo_de_datos') },
    ];

    constructor(
        public dialogRef: MatDialogRef<ImgModalComponent>,
        @Inject(MAT_DIALOG_DATA) public params: IMAGE_MANAGER_DIALOG_DATA,
        private store: Store,
        private dialog: MatDialog,
        private fb: FormBuilder,
        private translate: TranslateService,
        private toast: McToastService,
        private clipboardService: ClipboardService
    ) {
        this.isFileUpload = this.params.isFileUpload ?? false;
        if (params.singleSelection) {
            this.singleSelection = params.singleSelection;
        }
        
        // Inicializar formularios inmediatamente para evitar errores en el template
        const defaultModule = this.params.module ?? MODULES_OPTIONS.OUT_MODULE;
        this.searchForm = this.fb.group({
            searchValue: [''],
            module: [defaultModule],
            priority: [PRIORITY.DESC],
        });
        
        this.optimizationForm = this.fb.group({
            status: [false],
        });

        const actions = [new GetOptimizationConfig()];
        if(this.isFileUpload) {
            actions.push(new GetFilesDocs());
            actions.push(new GetFilesDocsSize());
        } else {
            actions.push(new GetImagesList());
        }
        
        this.store.dispatch(actions);

        this.modulesOptions = [
            // {
            //     id: MODULES_OPTIONS.ALL,
            //     name: `${this.translate.instant('app.global.modals.image_manager.filters.module_options.all')}`,
            //     disabled: false,
            // },
            {
                id: MODULES_OPTIONS.PPRODUCT_IMAGE,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.module_options.product-image'
                )}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.PRODUCT_CATEGORY,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.module_options.product-category'
                )}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.EDITOR,
                name: `${this.translate.instant('app.global.modals.image_manager.filters.module_options.editor')}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.OUT_MODULE,
                name: `${this.translate.instant('app.global.modals.image_manager.filters.module_options.out_module')}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.LOGO_FAVICON,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.module_options.logo_favicon'
                )}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.POST_EDITOR,
                name: `${this.translate.instant('app.global.modals.image_manager.filters.module_options.post-editor')}`,
                disabled: false,
            },
            {
                id: MODULES_OPTIONS.SITE_FILES,
                name: `${this.translate.instant('app.global.modals.image_manager.filters.module_options.site_files')}`,
                disabled: false,
            },
        ];

        this.priorityOptions = [
            {
                id: PRIORITY.DESC,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.order_options_gestor.mas_nuevo'
                )}`,
                disabled: false,
            },
            {
                id: PRIORITY.ASC,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.order_options_gestor.mas_viejo'
                )}`,
                disabled: false,
            },
            {
                id: PRIORITY.A_Z,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.order_options_gestor.nombre'
                )}`,
                disabled: false,
            },
            {
                id: PRIORITY.Z_A,
                name: `${this.translate.instant(
                    'app.global.modals.image_manager.filters.order_options_gestor.-nombre'
                )}`,
                disabled: false,
            },
        ];

        this.imagesFilter$.subscribe((filter: ImagesFilter) => {
            if (filter && !this.isFileUpload) {
                this.fallbackStatus = {};
                this.imagesFilter = filter;
                this.currentModule = filter.module;
                if (this.firstLoad) {
                    this.loading = true;
                    const currentFilters = this.imagesFilter;
                    const filterParams = {
                        ...currentFilters,
                        module: this.params.module,
                        search: null,
                        start: 1,
                        limit: 10,
                        order: 'id',
                        priority: PRIORITY.DESC,
                    };
                    this.firstLoad = false;
                    this.store.dispatch([new SetFilters(filterParams), new GetImagesList()]).subscribe({
                        complete: () => {
                            this.loading = false;
                            this.buildForm();
                        },
                    });
                }
            }
        });

        this.filesFilter$.subscribe((filter: ImagesFilter) => {
            // VALIDAR
            if (filter && this.isFileUpload) {
                this.fallbackStatus = {};
                this.filesFilter = filter;
                this.currentModule = filter.module;
                if (this.firstLoad) {
                    this.loading = true;
                    const currentFilters = this.filesFilter;
                    const filterParams = {
                        ...currentFilters,
                        module: this.params.module,
                        search: null,
                        start: 1,
                        limit: 10,
                        order: 'id',
                        priority: PRIORITY.DESC,
                    };
                    this.firstLoad = false;
                    this.store.dispatch([new SetFilters(filterParams, true), new GetFilesDocs()]).subscribe({
                        complete: () => {
                            this.loading = false;
                            this.buildForm();
                        },
                    });
                }
            }
        });

        this.imageList$.subscribe((list: ImageItem[]) => {
            if (list) {
                this.fallbackStatus = {};
                this.selectedFiles.clear();
                this.imageList = list;
                if (this.addedFiles.length) {
                    this.addedFiles.forEach((file) => {
                        const selectedImages = Array.from(this.selectedFiles);
                        const fileToSelect = this.imageList.find((img) => img.id === file.id);
                        if (fileToSelect && !selectedImages.some((s) => s.id === fileToSelect?.id)) {
                            if (this.singleSelection && this.selectedFiles.size === 0) {
                                this.selectedFiles.add(fileToSelect);
                            }

                            if (!this.singleSelection) {
                                this.selectedFiles.add(fileToSelect);
                            }
                        }
                    });
                }
            }
        });

        this.fileList$.subscribe((list: FileItem[]) => {
            // VALIDAR
            if (list) {
                this.fallbackStatus = {};
                this.selectedFiles.clear();
                this.fileList = list;
                if (this.addedFiles.length) {
                    this.addedFiles.forEach((file) => {
                        const selectedFiles = Array.from(this.selectedFiles);
                        const fileToSelect = this.fileList.find((f) => f.id === file.id);
                        if (fileToSelect && !selectedFiles.some((s) => s.id === fileToSelect?.id)) {
                            if (this.singleSelection && this.selectedFiles.size === 0) {
                                this.selectedFiles.add(fileToSelect);
                            }

                            if (!this.singleSelection) {
                                this.selectedFiles.add(fileToSelect);
                            }
                        }
                    });
                }
            }
        });

        this.imageListSize$.subscribe((size: number) => {
            if (size) {
                this.imagesPaginator = {
                    length: size,
                    pageSize: 10,
                    pageIndex: this.imagesFilter.start - 1,
                };
                this.fallbackStatus = {};
            }
        });

        this.fileListSize$.subscribe((size: number) => {
            if (size) {
                this.filesPaginator = {
                    length: size,
                    pageSize: 10,
                    pageIndex: this.filesFilter.start - 1,
                };
                this.fallbackStatus = {};
            }
        });

        this.optimizationConfig$.subscribe((status: boolean) => {
            this.buildOptimizationForm(status);
        });

        this.sizeData$.subscribe((data: SizeStatus) => {
            if(data) {
                this.setSizeStatus(data);
            }
        });
    }

    buildForm() {
        const defaultModule = this.params.module ?? MODULES_OPTIONS.OUT_MODULE;
        // Actualizar valores del formulario existente en lugar de recrearlo
        if (this.searchForm) {
            this.searchForm.patchValue({
                searchValue: '',
                module: defaultModule,
                priority: PRIORITY.DESC,
            }, { emitEvent: false });
        } else {
            this.searchForm = this.fb.group({
                searchValue: [''],
                module: [defaultModule],
                priority: [PRIORITY.DESC],
            });
        }

        this.searchForm
            .get('searchValue')
            .valueChanges.pipe(debounceTime(500))
            .subscribe((value) => {
                // Si el usuario escribe, limpiar el chip seleccionado
                if (this.selectedChipId && value !== this.selectedChipId) {
                    this.selectedChipId = null;
                }
                this.onSearchChange(value);
            });
    }

    buildOptimizationForm(status: boolean | string) {
        const fixedStatus = status === '0' || !status ? false : true;
        
        // Actualizar valores del formulario existente en lugar de recrearlo
        if (this.optimizationForm) {
            this.optimizationForm.patchValue({
                status: fixedStatus
            }, { emitEvent: false });
        } else {
            this.optimizationForm = this.fb.group({
                status: [fixedStatus],
            });

            this.optimizationForm
                .get('status')
                .valueChanges.pipe(debounceTime(500))
                .subscribe((value) => {
                    this.store.dispatch(new SetOptimizationConfig(value));
                });
        }
    }

    closeDialog() {
        this.clearFilters(true);
        this.resetSelection();
        /**
         * Si cierra el modal del gestor presionando cancelar pero se eliminaron imagenes
         * el gestor va a devolver al modulo que lo invoco el listado de imagenes eliminadas
         * para que el componente pueda actualizar las imagenes que tiene seleccionadas
         * y luego se limpia del store el listado de imagenes eliminadas
         */
        this.sendData();
    }

    sendData() {
        const selectedImages = Array.from(this.selectedFiles);
        const deletedFiles = this.store.selectSnapshot(ImagesState.getDeletedImages);

        const foreignModuleImages: any = selectedImages.filter((img) => img.module !== this.params.module);

        if (foreignModuleImages?.length) {
            this.loading = true;
            const formData: FormData = new FormData();

            foreignModuleImages.forEach((img, index) => {
                formData.append(`data[${index}][file]`, img.url);
                formData.append(`data[${index}][relation_id]`, this.params.relation_id.toString());
                formData.append(`data[${index}][module]`, this.params.module);
                formData.append(`data[${index}][type]`, 'image');
                formData.append(`data[${index}][url]`, 'true');
                this.selectedFiles.delete(img);
            });

            this.store.dispatch(new UploadPhoto(formData)).subscribe({
                complete: () => {
                    this.loading = false;
                    const uploadedFiles = this.store.selectSnapshot(ImagesState.getUploadedFiles);
                    uploadedFiles?.forEach((f: any) => {
                        this.selectedFiles.add(f);
                    });
                    const managerUpdatedData: ImageManagerData = {
                        selectedFiles: Array.from(this.selectedFiles),
                        deletedImages: deletedFiles,
                    };
                    this.store.dispatch([new ClearDeletedImages(), new ResetUploadedFiles()]);
                    this.dialogRef.close(managerUpdatedData);
                },
                error: () => (this.loading = false),
            });
        } else {
            const managerData: ImageManagerData = { selectedFiles: selectedImages, deletedImages: deletedFiles };
            this.store.dispatch(new ClearDeletedImages());
            this.dialogRef.close(managerData);
        }
    }

    openUploadDialog(): void {
        const data = this.params;
        const image_upload_manager = this.dialog.open(UploadImagesComponent, { data });

        image_upload_manager.afterClosed().subscribe((urls: UploadedItem[]) => {
            if (urls) {
                if (this.singleSelection && this.selectedFiles.size === 0) {
                    this.addedFiles = this.addedFiles.concat(urls[0]);
                }

                if (!this.singleSelection) {
                    this.addedFiles = this.addedFiles.concat(urls);
                }
                this.clearFilters();
            }
        });
    }

    handlePageEvent(event: PageEvent) {
        this.fallbackStatus = {};
        this.resetSelection();
        const { length, pageSize, pageIndex } = event;
        const currentFilters = this.isFileUpload ? this.filesFilter : this.imagesFilter;
        const filterParams = {
            ...currentFilters,
            limit: pageSize,
            start: pageIndex + 1,
        };
        if(this.isFileUpload) {
            this.filesPaginator = { length, pageSize, pageIndex };
        } else {
            this.imagesPaginator = { length, pageSize, pageIndex };
        }
        if(this.isFileUpload) {
            this.store.dispatch([new SetFilters(filterParams, true), new GetFilesDocs()]);
        } else {
            this.store.dispatch([new SetFilters(filterParams), new GetImagesList()]);
        }
    }

    onSearchChange(value: string) {
        this.fallbackStatus = {};
        this.resetSelection();
        this.filtering = true;
        const currentFilters = this.isFileUpload ? this.filesFilter : this.imagesFilter;
        const filterParams = {
            ...currentFilters,
            search: value,
            start: 1,
        };
        this.store.dispatch([new SetFilters(filterParams, this.isFileUpload), this.isFileUpload ? new GetFilesDocs() : new GetImagesList()]).subscribe({
            complete: () => (this.filtering = false),
        });
    }

    setFilters(event, filterType: FILTER_TYPE) {
        this.fallbackStatus = {};
        this.resetSelection();
        this.filtering = true;
        const currentFilters = this.isFileUpload ? this.filesFilter : this.imagesFilter;
        const value = event.target.value;
        const filterParams = {
            ...currentFilters,
            start: 1,
        };
        switch (filterType) {
            case FILTER_TYPE.MODULE:
                filterParams.module = value;
                this.isFileUpload = value === MODULES_OPTIONS.SITE_FILES;
                this.selectedChipId = null;
                this.selectedFiles.clear();
                break;

            case FILTER_TYPE.PRIORITY:
                if (value === 'A-Z' || value === 'Z-A') {
                    filterParams.priority = value === 'A-Z' ? PRIORITY.ASC : PRIORITY.DESC;
                    filterParams.order = 'filename';
                } else {
                    filterParams.priority = value;
                    filterParams.order = 'id';
                }
                break;
        }
        const action = this.isFileUpload ? new GetFilesDocs() : new GetImagesList();
        this.store.dispatch([new SetFilters(filterParams, this.isFileUpload), action]).subscribe({
            complete: () => (this.filtering = false),
        });
    }

    clearFilters(onlyFilter?: boolean): void {
        this.fallbackStatus = {};
        this.filtering = true;
        const defaultFilter: ImagesFilter = {
            search: null,
            start: 1,
            limit: 10,
            order: 'id',
            priority: PRIORITY.DESC,
            module:
                this.params?.module !== this.currentModule
                    ? this.currentModule
                    : this.params?.module ?? MODULES_OPTIONS.OUT_MODULE,
        };
        const actions = onlyFilter
            ? [new SetFilters(defaultFilter, this.isFileUpload)]
            : [new SetFilters(defaultFilter, this.isFileUpload), this.isFileUpload ? new GetFilesDocs() : new GetImagesList()];
        this.searchForm.get('searchValue').setValue(null, { emitEvent: false });
        this.searchForm.get('module').setValue(defaultFilter.module, { emitEvent: false });
        this.searchForm.get('priority').setValue(this.priorityOptions[1].id, { emitEvent: false });
        this.selectedChipId = null;
        this.store.dispatch(actions).subscribe({
            complete: () => (this.filtering = false),
        });
    }

    isSelected(image: ImageItem): boolean {
        return this.selectedFiles.has(image);
    }

    toggleFileSelection(image: ImageItem): void {
        if (this.singleSelection) {
            if (this.selectedFiles.has(image)) {
                this.selectedFiles.delete(image);
            } else {
                this.selectedFiles.clear();
                this.selectedFiles.add(image);
            }
        } else {
            if (this.selectedFiles.has(image)) {
                this.selectedFiles.delete(image);
            } else {
                this.selectedFiles.add(image);
            }
        }
    }

    deletePhotos(): void {
        const selectedImages = Array.from(this.selectedFiles);

        // Implementa la lógica para eliminar las imágenes seleccionadas
        const payload: DeleteImages = {
            delete: [],
        };

        const deleteWarning: { name: string; modules: string[] }[] = [];

        selectedImages.forEach((img) => {
            payload.delete.push({
                resources_id: img.id,
                module: img.module,
            });

            if (img.use_module?.length) {
                const modulesNames: string[] = [];
                img.use_module.forEach((m) => {
                    const name: string = this.modulesOptions.find((option) => option.id === m)?.name;
                    modulesNames.push(name);
                });
                const warningItem: { name: string; modules: string[] } = {
                    name: img.filename,
                    modules: modulesNames,
                };
                deleteWarning.push(warningItem);
            }
        });

        if (deleteWarning?.length) {
            const warningModal = this.dialog.open(UsedImagesModalComponent, {
                disableClose: true,
                data: { warnings: deleteWarning, isFile: this.isFileUpload },
            });
            warningModal.afterClosed().subscribe((userConfirm: boolean) => {
                if (userConfirm) {
                    this.deleteImages(payload);
                }
            });
        } else {
            const modalTitle = this.getDeleteModalTitle(payload.delete.length, selectedImages, payload.delete[0]?.resources_id);
            const data: { title: string; btnText: string; actionDescription?: string } = {
                title: modalTitle,
                btnText: this.translate.instant('app.global.modals.image_manager.delete-images-modal.buttons.accept'),
            };
            this.dialog
                .open(SsConfirmComponent, { data })
                .afterClosed()
                .subscribe((response: boolean) => {
                    if (response) {
                        this.deleteImages(payload);
                    }
                });
        }
    }

    private getDeleteModalTitle(deleteCount: number, selectedImages: (ImageItem | FileItem)[], firstResourceId?: number): string {
        const isSingle = deleteCount === 1;
        const fileName = isSingle && firstResourceId 
            ? selectedImages.find((img) => img.id === firstResourceId)?.filename 
            : null;

        if (isSingle) {
            const translationKey = this.isFileUpload
                ? 'app.global.modals.image_manager.delete-images-modal.single_title_file'
                : 'app.global.modals.image_manager.delete-images-modal.single_title';
            const placeholder = this.isFileUpload ? '{{file}}' : '{{img}}';
            return this.translate.instant(translationKey).replace(placeholder, fileName || '');
        } else {
            const translationKey = this.isFileUpload
                ? 'app.global.modals.image_manager.delete-images-modal.multiple_title_file'
                : 'app.global.modals.image_manager.delete-images-modal.multiple_title';
            return this.translate.instant(translationKey);
        }
    }

    deleteOnePhoto(img: ImageItem): void {
        const payload: DeleteImages = {
            delete: [
                {
                    resources_id: img.id,
                    module: img.module,
                },
            ],
        };

        const deleteWarning: { name: string; modules: string[] }[] = [];

        if (img.use_module?.length) {
            const modulesNames: string[] = [];
            img.use_module.forEach((m) => {
                const name: string = this.modulesOptions.find((option) => option.id === m)?.name;
                modulesNames.push(name);
            });
            const warningItem: { name: string; modules: string[] } = {
                name: img.filename,
                modules: modulesNames,
            };
            deleteWarning.push(warningItem);
        }

        if (deleteWarning?.length) {
            const warningModal = this.dialog.open(UsedImagesModalComponent, {
                disableClose: true,
                data: { warnings: deleteWarning, isFile: this.isFileUpload },
            });
            warningModal.afterClosed().subscribe((userConfirm: boolean) => {
                if (userConfirm) {
                    this.deleteImages(payload);
                }
            });
        } else {
            const modalTitle = this.getDeleteModalTitle(1, [img], img.id);
            const data: { title: string; btnText: string; actionDescription?: string } = {
                title: modalTitle,
                btnText: this.translate.instant('app.global.modals.image_manager.delete-images-modal.buttons.accept'),
            };
            this.dialog
                .open(SsConfirmComponent, { data })
                .afterClosed()
                .subscribe((response: boolean) => {
                    if (response) {
                        this.deleteImages(payload);
                    }
                });
        }
    }

    private deleteImages(payload: DeleteImages): void {
        this.store.dispatch(new DeleteImage(payload, this.isFileUpload)).subscribe({
            complete: () => {
                this.clearFilters();
                this.resetSelection();
                if (this.isFileUpload) {
                    this.selectedChipId = null;
                    this.store.dispatch([new GetFilesDocs(), new GetFilesDocsSize()]);
                }
            },
        });
    }

    cropImage(index: number): void {
        const imageToCrop: ImageItem = this.imageList[index];
        const img = new Image();
        img.src = imageToCrop.url;

        const dialogRef = this.dialog.open(McCropperComponent, {
            width: '100vw',
            disableClose: true,
            data: { file: img, name: `${imageToCrop.filename}-crop`, type: `${imageToCrop.filetype}` },
        });
        dialogRef.afterClosed().subscribe((data: { cropedFile: File; base64: string }) => {
            if (data) {
                this.loading = true;
                const formData: FormData = new FormData();
                formData.append(`data[0][file]`, data.cropedFile, `${imageToCrop.filename}-crop`);
                formData.append(`data[0][relation_id]`, this.params.relation_id.toString());
                formData.append(`data[0][module]`, this.params.module);
                formData.append(`data[0][type]`, 'image');

                this.store.dispatch(new UploadPhoto(formData)).subscribe({
                    complete: () => {
                        this.loading = false;
                        this.clearFilters();
                    },
                    error: () => (this.loading = false),
                });
            }
        });
    }

    toggleView() {
        this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
    }

    getThumb(url: string): string {
        const splitedUrl: string[] = url.split('.webp');
        const thumbUrl: string = splitedUrl?.length ? `${splitedUrl[0]}-240.webp` : url;
        return thumbUrl;
    }

    setDefaultUrl(e, defaultUrl: string, id: number): void {
        if (!this.fallbackStatus[id]) {
            e.target.src = defaultUrl;
            this.fallbackStatus[id] = true;
        } else {
            e.target.src = `${this.assetsUrl}${this.mfsPath}/img/front/images/image404.svg`;
        }
    }

    openCarouselModal(index: number): void {
        this.dialog.open(CarouselModalComponent, {
            data: { images: this.imageList, index },
            width: '80%',
            // height: '80%',
            panelClass: 'full-image-modal',
        });
    }

    openImagePreviewModal(imageUrl: string): void {
        this.dialog.open(ImagePreviewModalComponent, {
            data: { imageUrl },
            width: '80%',
            height: '80%',
            panelClass: 'full-image-modal',
        });
    }

    resetSelection(): void {
        this.selectedFiles.clear();
        this.selectedFilesItems.clear();
    }

    /**
     * Gestión de archivos
     */
    
    /**
     * Selecciona un chip de tipo de archivo y actualiza los filtros
     * @param chipId - ID del chip de tipo de archivo a seleccionar (FILE_TYPE_FILTER)
     */
    selectFileTypeChip(chipId: string): void {
        // Si se selecciona el mismo chip, deseleccionarlo
        if (this.selectedChipId === chipId) {
            this.selectedChipId = null;
            // Limpiar el filtro
            // this.searchForm.get('searchValue').setValue('', { emitEvent: false });
            // this.onSearchChange('');
            this.searchForm.get('searchValue').setValue('');
            return;
        }

        // Seleccionar el nuevo chip
        this.selectedChipId = chipId;

        // Actualizar searchForm.searchValue con el id del chip (sin emitir evento para evitar loop)
        // this.searchForm.get('searchValue').setValue(chipId, { emitEvent: false });
        this.searchForm.get('searchValue').setValue(chipId);

        // Actualizar filesFilter.search con el id del chip
        // this.onSearchChange(chipId);
    }

    /**
     * Abre la ventana de selección de archivos
     * @param element - Elemento HTMLInputElement del input file
     */
    openFileUploadWindow(element: HTMLInputElement): void {
        if (!this.isFileUploadWindowOpen) {
            this.isFileUploadWindowOpen = true;
            element.click();

            element.addEventListener('cancel', (evt) => {
                this.isFileUploadWindowOpen = false;
            });
        }
    }

    /**
     * Maneja el evento de selección de archivos desde el input
     * @param event - Evento del input file con los archivos seleccionados
     */
    fileBrowseHandler(event: any) {
        const files = Array.from(event.target.files || []) as File[];
        this.prepareFilesList(files);
        this.isFileUploadWindowOpen = false;
    }

    /**
     * Prepara y valida la lista de archivos seleccionados antes de subirlos
     * @param files - Array de archivos File seleccionados por el usuario
     */
    prepareFilesList(files: Array<File>) {
        const selectedFiles = this.selectedFilesItems.size + files.length;
        if (selectedFiles > 15) {
            this.toast.danger(this.translate.instant('app.producto.toast.error_max_imagen', { limit: 15 }));
            return;
        }

        // Validar que los archivos no excedan el espacio disponible
        const BYTES_TO_MB = 1024 * 1024; // 1 MB = 1048576 bytes
        
        // Calcular el tamaño total de los archivos ya seleccionados (en MB)
        let existingFilesSizeMB = 0;
        this.selectedFilesItems.forEach((fileData) => {
            existingFilesSizeMB += fileData.file.size / BYTES_TO_MB;
        });

        // Calcular el tamaño total de los nuevos archivos (en MB)
        let newFilesSizeMB = 0;
        files.forEach((file) => {
            newFilesSizeMB += file.size / BYTES_TO_MB;
        });

        // Tamaño total que se intentaría usar
        const totalSizeMB = existingFilesSizeMB + newFilesSizeMB;

        // Validar si excede el espacio disponible
        if (totalSizeMB > this.sizeStatus.free) {
            if (files.length === 1) {
                this.toast.danger(this.translate.instant('app.global.modals.image_manager.toasts.file_save_error_msg_limit'));
            } else {
                this.toast.danger(this.translate.instant('app.global.modals.image_manager.toasts.file_save_error_msg_limit_multiple'));
            }
            return;
        }
        
        const filePromises: Promise<void>[] = [];
        
        for (const item of files) {
            // Obtener la extensión del archivo
            const fileName = item.name.toLowerCase();
            const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
            
            if (this.acceptedFileTypes.includes(fileExtension)) {
                const filePromise = new Promise<void>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e: any) => {
                        const url = e.target.result;
                        const fileData = { file: item, url };
                        this.files.push(fileData);
                        if (this.selectedFilesItems.size < 15) {
                            this.selectedFilesItems.add(fileData);
                        }
                        resolve();
                    };
                    reader.onerror = () => {
                        reject(new Error(`Error al leer el archivo ${item.name}`));
                    };
                    reader.readAsDataURL(item);
                });
                filePromises.push(filePromise);
            } else {
                this.toast.danger(
                    this.translate.instant(
                        'app.global.modals.image_manager.upload_image_modal.computer.invalid_image',
                        { img: item.name }
                    )
                );
            }
        }
        
        // Esperar a que todos los archivos se hayan procesado antes de llamar a uploadFiles
        Promise.all(filePromises)
            .then(() => {
                this.uploadFiles();
            })
            .catch((error) => {
                console.error('Error al procesar archivos:', error);
                this.toast.danger('Error al procesar uno o más archivos');
            });
    }

    /**
     * Sube los archivos seleccionados al servidor
     * No recibe parámetros, usa this.selectedFilesItems para obtener los archivos
     */
    uploadFiles(): void {
        const selectedFiles = Array.from(this.selectedFilesItems);

        // Validar que hay archivos para subir
        if (selectedFiles.length === 0) {
            this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar') || 'No hay archivos seleccionados para subir');
            return;
        }

        this.loading = true;
        const formData: FormData = new FormData();

        selectedFiles.forEach((fileData, index) => {
            formData.append(`data[${index}][file]`, fileData.file, fileData.file.name);
            formData.append(`data[${index}][relation_id]`, this.params.relation_id.toString());
            formData.append(`data[${index}][module]`, MODULES_OPTIONS.SITE_FILES);
            formData.append(`data[${index}][type]`, 'resources');
        });

        this.store.dispatch(new UploadPhoto(formData, true, true)).subscribe({
            complete: () => {
                this.loading = false;
                // Limpiar los archivos seleccionados después de subirlos
                this.selectedFilesItems.clear();
                this.files = [];
                this.selectedChipId = null;
                this.store.dispatch([new GetFilesDocs(), new GetFilesDocsSize()]);
            },
            error: () => {
                this.loading = false;
                this.selectedFilesItems.clear();
            },
        });
    }

    /**
     * Obtiene la clase de icono de Font Awesome según la extensión del archivo
     * @param filename - Nombre del archivo con su extensión
     * @returns Clase CSS de Font Awesome para el icono correspondiente
     */
    getFileIcon(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase() || '';
        
        switch (extension) {
            case 'xlsx':
            case 'xls':
            case 'csv':
                return 'fa fa-file-excel'; // Icono para hojas de cálculo
            case 'json':
            case 'xml':
            case 'html':
            case 'css':
                return 'fa fa-file-code'; // Icono para archivos de código
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
                return 'fa fa-file-image'; // Icono para imágenes
            case 'pdf':
                return 'fa fa-file-pdf';
            case 'doc':
            case 'docx':
            case 'odt':
            case 'rtf':
                return 'fa fa-file-word';
            case 'ppt':
            case 'pptx':
                return 'fa fa-file-powerpoint';
            case 'zip':
            case 'rar':
            case 'gz':
            case '7z':
                return 'fa fa-file-archive';
            case 'mp3':
            case 'wav':
                return 'fa fa-file-audio';
            case 'mp4':
            case 'avi':
            case 'mov':
                return 'fa fa-file-video';
            case 'txt':
                return 'fa fa-file-alt';
            default:
                return 'fa fa-file';
        }
    }

    /**
     * Obtiene la clase CSS para el color del icono según la extensión del archivo
     * @param filename - Nombre del archivo con su extensión
     * @returns Clase CSS para el color del icono
     */
    getFileIconColorClass(filename: string): string {
        const extension = filename.split('.').pop()?.toLowerCase() || '';
        
        switch (extension) {
            case 'pdf':
                return 'file-icon-red'; // Rojo para PDF
            case 'xlsx':
            case 'xls':
            case 'csv':
                return 'file-icon-green'; // Verde para hojas de cálculo
            case 'json':
            case 'xml':
            case 'html':
            case 'css':
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
                return 'file-icon-black'; // Negro para código e imágenes
            default:
                return 'file-icon-black';
        }
    }

    /**
     * Elimina un archivo individual después de confirmar con el usuario
     * @param file - Objeto FileItem que representa el archivo a eliminar
     */
    deleteOneFile(file: FileItem): void {
        const payload: DeleteImages = {
            delete: [
                {
                    resources_id: file.id,
                    module: file.module,
                },
            ],
        };

        const deleteWarning: { name: string; modules: string[] }[] = [];

        if (file.use_module?.length) {
            const modulesNames: string[] = [];
            file.use_module.forEach((m) => {
                const name: string = this.modulesOptions.find((option) => option.id === m)?.name;
                modulesNames.push(name);
            });
            const warningItem: { name: string; modules: string[] } = {
                name: file.filename,
                modules: modulesNames,
            };
            deleteWarning.push(warningItem);
        }

        if (deleteWarning?.length) {
            const warningModal = this.dialog.open(UsedImagesModalComponent, {
                disableClose: true,
                data: { warnings: deleteWarning, isFile: this.isFileUpload },
            });
            warningModal.afterClosed().subscribe((userConfirm: boolean) => {
                if (userConfirm) {
                    this.deleteImages(payload);
                }
            });
        } else {
            const data: { title: string; btnText: string; actionDescription?: string } = {
                title: this.translate
                    .instant('app.global.modals.image_manager.delete-files-modal.single_title')
                    .replace('{{file}}', `${file.filename}`),
                btnText: this.translate.instant('app.global.modals.image_manager.delete-files-modal.buttons.accept'),
            };
            this.dialog
                .open(SsConfirmComponent, { data })
                .afterClosed()
                .subscribe((response: boolean) => {
                    if (response) {
                        this.deleteImages(payload);
                    }
                });
        }
    }

    mouseEnter(trigger) {
        if (this.timedOutCloser) {
            clearTimeout(this.timedOutCloser);
        }
        trigger.openMenu();
    }

    mouseLeave(trigger) {
        this.timedOutCloser = setTimeout(() => {
            trigger.closeMenu();
        }, 50);
    }

    copyToClipboard(event: MouseEvent, url: string): void {
        event.preventDefault();
        event.stopPropagation();
        this.clipboardService.copy(url);
        this.toast.success(this.translate.instant('app.ecommerce_metodos_de_pago.paypal.url_copiada'));
    }

    /**
     * Descarga un archivo desde su URL y lo guarda con su nombre original
     * @param file - Objeto FileItem que contiene la información del archivo a descargar
     * @returns Promise que se resuelve cuando la descarga se completa
     */
    async downloadFile(file: FileItem): Promise<void> {
        try {
            this.loading = true;
            
            // Obtener el archivo como blob
            const response = await fetch(file.url);
            if (!response.ok) {
                throw new Error(`Error al descargar el archivo: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            
            // Crear un enlace temporal para descargar el archivo
            const downloadLink = document.createElement('a');
            const blobUrl = window.URL.createObjectURL(blob);
            
            downloadLink.href = blobUrl;
            downloadLink.download = file.filename; // Usar el nombre del archivo del objeto FileItem
            downloadLink.style.display = 'none';
            
            // Agregar al DOM, hacer click y remover
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            
            // Liberar el objeto URL
            window.URL.revokeObjectURL(blobUrl);
            
            this.toast.success(this.translate.instant('app.global.toast.descarga_exitosa') || 'Archivo descargado correctamente');
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            this.toast.danger(this.translate.instant('app.global.toast.no_se_pudo_realizar') || 'No se pudo descargar el archivo');
        } finally {
            this.loading = false;
        }
    }

    /**
     * Establece el estado del tamaño de almacenamiento de archivos
     * @param response - Objeto SizeStatus con la información del tamaño usado y disponible
     */
    private setSizeStatus(response: SizeStatus): void {
        this.sizeStatus.used = response.used;
        this.sizeStatus.free = response.free;
        this.sizeStatus.max = response.max;
        // 🔹 Cálculos
        this.sizeStatus.usedPercentage = this.sizeStatus.max > 0 
            ? (this.sizeStatus.used / this.sizeStatus.max) * 100 
            : 0;
        this.sizeStatus.usedMB = parseFloat(this.sizeStatus.used.toFixed(2));
        this.sizeStatus.maxGB = parseFloat((this.sizeStatus.max / 1024).toFixed(2)); // convertir MB a GB

        // 🔹 Determinar color según porcentaje
        this.setProgressColor();
    }

    /**
     * Establece el color de la barra de progreso según el porcentaje de uso
     * No recibe parámetros, usa this.sizeStatus.usedPercentage para determinar el color
     */
    private setProgressColor(): void {
        const percentage = this.sizeStatus.usedPercentage || 0;
        
        if (percentage < 50) {
            this.progressColor = 'accent'; // verde
            this.progressBarClass = '';
        } else if (percentage < 80) {
            this.progressColor = 'primary'; // usar primary como base
            this.progressBarClass = 'medium-usage'; // aplicar clase naranja
        } else {
            this.progressColor = 'warn'; // rojo
            this.progressBarClass = '';
        }
    }
}
